// usePersonnelData.js
// ─────────────────────────────────────────────────────────────────────────────
//
// Manages all data-fetching for the Personnel Metrics page.
//
// ── Responsibilities ──────────────────────────────────────────────────────────
//
//   1. Fetch the full personnel list on mount (personnel-list route) and cache
//      it for the lifetime of the component.
//
//   2. Expose a filtered + sorted dropdown list derived from that cache:
//        • Before the user types  → top 10 personnel, sorted alphabetically
//          by name.
//        • While the user types   → personnel whose name, employeeNumber, or
//          department contains the query string (case-insensitive).
//
//   3. On person selection: call the personnel-metrics route with the selected
//      employeeNumber. Cancels any in-flight request if the user switches
//      selection.
//
//   4. URL sync: if ?employeeNumber=<id> is present on mount, auto-select that
//      person once the list has loaded and strip the param from the URL.
//
// ── Return shape ──────────────────────────────────────────────────────────────
//
//   {
//     // Search / dropdown controls
//     search:              string,
//     setSearch:           (value: string) => void,
//     dropdownOpen:        boolean,
//     setDropdownOpen:     (open: boolean) => void,
//     dropdownPersonnel:   Person[],          - filtered list for the dropdown
//
//     // Selection
//     selectedPerson:      Person | null,     - the raw personnel-list row
//     handleSelectPerson:  (person: Person) => void,
//     handleClearSelection: () => void,
//
//     // Personnel-metrics fetch state
//     personnelData:       PersonnelMetricsPayload | null,
//     metricsLoading:      boolean,
//     metricsError:        string | null,
//
//     // Personnel-list fetch state
//     listLoading:         boolean,
//     listError:           string | null,
//   }
//
// ── Type aliases (JSDoc only - no TS runtime) ─────────────────────────────────
//
//   Person = {
//     name:           string | null,
//     objId:          number | null,
//     employeeNumber: string | null,
//     department:     string | null,
//   }
//
//   PersonnelMetricsPayload = return type of fetchPersonnelMetrics (see dataReceiver.js)

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchPersonnelList,
  fetchPersonnelMetrics,
} from "../../utils/dataReceiver";

// ── Constants ─────────────────────────────────────────────────────────────────

/** How many personnel to show in the dropdown before the user starts typing. */
const DROPDOWN_DEFAULT_LIMIT = 10;

// ─────────────────────────────────────────────────────────────────────────────

export function usePersonnelData() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Personnel list ──────────────────────────────────────────────────────────
  /** @type {[Person[], Function]} */
  const [personnelList, setPersonnelList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Search / dropdown ───────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // ── Metrics fetch state ─────────────────────────────────────────────────────
  const [personnelData, setPersonnelData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  // AbortController ref - cancelled on selection change, clear, or unmount.
  const metricsAbortRef = useRef(null);

  // ── 1. Fetch personnel list on mount ───────────────────────────────────────
  //
  // Uses a dedicated AbortController so the list fetch is cancelled cleanly
  // on unmount without touching the metrics abort ref.
  useEffect(() => {
    const controller = new AbortController();

    async function loadList() {
      setListLoading(true);
      setListError(null);
      try {
        const rows = await fetchPersonnelList(controller.signal);
        if (!controller.signal.aborted) {
          setPersonnelList(rows);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setListError(
          err?.message ?? "Failed to load the personnel list. Please refresh.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setListLoading(false);
        }
      }
    }

    loadList();
    return () => controller.abort();
  }, []);

  // ── 2. URL param auto-selection ─────────────────────────────────────────────
  //
  // Fires once the personnel list is populated and either a ?employeeNumber=
  // or ?name= param exists. employeeNumber takes priority when both are
  // present (exact, case-insensitive). Strips the param from the URL after
  // selection so navigation doesn't re-trigger it.
  //
  // Note: activePersonnel.js (and possibly other callers) link here using
  // ?name=<fullName> rather than employeeNumber, since that's the only
  // identifier surfaced in that view. Support both so auto-select works
  // regardless of which one the linking page has on hand.
  useEffect(() => {
    if (listLoading || personnelList.length === 0) return;

    const employeeNumberParam = searchParams.get("employeeNumber");
    const nameParam = searchParams.get("name");
    if ((!employeeNumberParam && !nameParam) || selectedPerson) return;

    const match = employeeNumberParam
      ? personnelList.find(
          (p) =>
            p.employeeNumber?.toLowerCase() ===
            employeeNumberParam.toLowerCase(),
        )
      : personnelList.find(
          (p) => p.name?.toLowerCase() === nameParam.toLowerCase(),
        );

    if (match) {
      setSearch(match.name ?? match.employeeNumber ?? "");
      setSelectedPerson(match);
      setSearchParams({}, { replace: true });
    }
  }, [
    listLoading,
    personnelList,
    searchParams,
    selectedPerson,
    setSearchParams,
  ]);

  // ── 3. Metrics fetch on selection ──────────────────────────────────────────
  //
  // Triggered whenever selectedPerson changes to a non-null value. Cancels
  // any prior in-flight request before starting the new one.
  useEffect(() => {
    if (!selectedPerson) {
      setPersonnelData(null);
      setMetricsError(null);
      return;
    }

    metricsAbortRef.current?.abort();
    const controller = new AbortController();
    metricsAbortRef.current = controller;

    async function loadMetrics() {
      setMetricsLoading(true);
      setMetricsError(null);
      setPersonnelData(null);
      try {
        const data = await fetchPersonnelMetrics(
          { employeeNumber: selectedPerson.employeeNumber },
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setPersonnelData(data);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setMetricsError(
          err?.message ??
            `Failed to load metrics for ${selectedPerson.name ?? selectedPerson.employeeNumber}. Please try again.`,
        );
      } finally {
        if (!controller.signal.aborted) {
          setMetricsLoading(false);
        }
      }
    }

    loadMetrics();

    return () => controller.abort();
  }, [selectedPerson]);

  // Abort any in-flight metrics fetch on unmount
  useEffect(() => {
    return () => {
      metricsAbortRef.current?.abort();
    };
  }, []);

  // ── 4. Dropdown list derivation ─────────────────────────────────────────────
  //
  // Before typing:  top DROPDOWN_DEFAULT_LIMIT personnel sorted alphabetically
  //                 by name. Entries with null names sort to the end.
  //
  // While typing:   any person whose name, employeeNumber, or department
  //                 contains the trimmed query (case-insensitive). No limit
  //                 applied so the user can see all matches.
  const dropdownPersonnel = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      return [...personnelList]
        .sort((a, b) => {
          if (!a.name && !b.name) return 0;
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        })
        .slice(0, DROPDOWN_DEFAULT_LIMIT);
    }

    return personnelList.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.employeeNumber?.toLowerCase().includes(q) ||
        p.department?.toLowerCase().includes(q),
    );
  }, [personnelList, search]);

  // ── 5. Interaction handlers ─────────────────────────────────────────────────

  /**
   * Called when the user clicks a dropdown item.
   * Sets the search text to the person's name and closes the dropdown.
   */
  const handleSelectPerson = useCallback((person) => {
    setSelectedPerson(person);
    setSearch(person.name ?? person.employeeNumber ?? "");
    setDropdownOpen(false);
  }, []);

  /**
   * Called when the user clicks the ✕ clear button.
   * Resets all selection and search state; cancels any in-flight fetch.
   */
  const handleClearSelection = useCallback(() => {
    metricsAbortRef.current?.abort();
    setSelectedPerson(null);
    setPersonnelData(null);
    setMetricsError(null);
    setSearch("");
    setDropdownOpen(false);
  }, []);

  // ── Return ──────────────────────────────────────────────────────────────────

  return {
    // Search / dropdown
    search,
    setSearch,
    dropdownOpen,
    setDropdownOpen,
    dropdownPersonnel,

    // Selection
    selectedPerson,
    handleSelectPerson,
    handleClearSelection,

    // Metrics fetch state
    personnelData,
    metricsLoading,
    metricsError,

    // List fetch state
    listLoading,
    listError,
  };
}
